import { useMemo, useState } from 'react';
import { SelectProps } from '@amzn/awsui-components-console/select/interfaces';
import { VenueInfoItem } from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { venueOptionLabel as venueOptionLabelFor } from '../utils/venueDisplay';

/**
 * Shared conference/venue scope state for the list pages (Launches, Groups, Active Packages) —
 * tcx-core-platforms-903 D6. Each page had a byte-identical copy of the two sentinels, the
 * conference/venue `useState`, the `conferences`/`venuesForConference` `useMemo`s, and the
 * Select option arrays; centralizing them here keeps the "All accessible / All venues" semantics
 * (and the `sentinel -> undefined` translation the APIs expect) in one place (finding on CR-301380769).
 *
 * Display-only: the dropdown labels route through `venueOptionLabel` (coordinator canonical name),
 * but every option VALUE stays the raw venue, so scoping/identity are unchanged (D2).
 */

// Sentinel for "all conferences the caller can access" (D6 default — the backend serves an admin
// every conference and a non-admin the union of the conferences they can read).
export const ALL_CONFERENCES = '__ALL_CONFERENCES__';
// Sentinel for "all venues within the selected conference".
export const ALL_VENUES = '__ALL_VENUES__';

export interface ConferenceVenueScope {
  /** Raw selected conference (may be the ALL_CONFERENCES sentinel). */
  conference: string;
  /** Raw selected venue (may be the ALL_VENUES sentinel). */
  venue: string;
  /** Set the venue directly (values come from the venue Select options). */
  setVenue: (venue: string) => void;
  /** Select a conference; resets the venue to ALL_VENUES within the new conference. */
  selectConference: (conference?: string) => void;
  /** Distinct, sorted conference ids derived from the venue list. */
  conferences: string[];
  /** Raw venue names within the selected conference, sorted. Empty for the ALL_CONFERENCES scope. */
  venuesForConference: string[];
  conferenceOptions: SelectProps.Option[];
  selectedConferenceOption: SelectProps.Option;
  venueOptions: SelectProps.Option[];
  selectedVenueOption: SelectProps.Option;
  /** Conference to send to the API: undefined for the ALL_CONFERENCES scope. */
  scopedConference?: string;
  /** Venue to send to the API: undefined for the ALL_VENUES scope. */
  scopedVenue?: string;
}

export function useConferenceVenueScope(
  venues: VenueInfoItem[]
): ConferenceVenueScope {
  const [conference, setConference] = useState<string>(ALL_CONFERENCES);
  const [venue, setVenue] = useState<string>(ALL_VENUES);

  // Distinct, sorted conference ids from the venue list.
  const conferences = useMemo(() => {
    const set = new Set<string>();
    venues.forEach((v) => {
      if (v.conference) set.add(v.conference);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [venues]);

  // Venues within the currently-selected conference, sorted. Empty for the "All accessible" scope.
  const venuesForConference = useMemo(() => {
    if (conference === ALL_CONFERENCES) return [];
    return venues
      .filter((v) => v.conference === conference && v.venue)
      .map((v) => v.venue)
      .sort((a, b) => a.localeCompare(b));
  }, [venues, conference]);

  // Dropdown label for a venue within the currently-selected conference: when a coordinator canonical
  // override exists (and differs from the raw name), disambiguate as "<canonicalVenueName> (<venue>)";
  // otherwise just the raw venue. The option VALUE stays the raw venue. tcx-core-platforms-903 D2.
  const venueOptionLabel = (ven: string): string =>
    venueOptionLabelFor(venues, conference, ven);

  const conferenceOptions: SelectProps.Option[] = [
    { label: 'All accessible', value: ALL_CONFERENCES },
    ...conferences.map((c) => ({ label: c, value: c })),
  ];
  const selectedConferenceOption: SelectProps.Option =
    conference === ALL_CONFERENCES
      ? { label: 'All accessible', value: ALL_CONFERENCES }
      : { label: conference, value: conference };

  const venueOptions: SelectProps.Option[] = [
    { label: 'All venues', value: ALL_VENUES },
    ...venuesForConference.map((v) => ({
      label: venueOptionLabel(v),
      value: v,
    })),
  ];
  const selectedVenueOption: SelectProps.Option =
    venue === ALL_VENUES
      ? { label: 'All venues', value: ALL_VENUES }
      : { label: venueOptionLabel(venue), value: venue };

  // Changing conference resets the venue to "all venues" within it.
  const selectConference = (value?: string) => {
    setConference(value ?? ALL_CONFERENCES);
    setVenue(ALL_VENUES);
  };

  return {
    conference,
    venue,
    setVenue,
    selectConference,
    conferences,
    venuesForConference,
    conferenceOptions,
    selectedConferenceOption,
    venueOptions,
    selectedVenueOption,
    scopedConference: conference === ALL_CONFERENCES ? undefined : conference,
    scopedVenue: venue === ALL_VENUES ? undefined : venue,
  };
}
